import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ADMIN_ACCOUNT_STATUSES,
  ADMIN_APP_ROLES,
  ADMIN_WORKSPACE_PERSONAS,
  isAccountPaused,
  type AccountStatus,
  type AppRole,
} from "@/config/adminAccountOptions";
import type { WorkspacePersona } from "@/config/workspacePersona";
import {
  pauseAccount,
  reactivateAccount,
  setAccountAppRoles,
  setAccountPersona,
  setAccountStatus,
} from "@/services/systemAdminService";
import type { AdminAccountRow } from "@/types/systemAdmin";

interface AccountActionsCellProps {
  account: AdminAccountRow;
  currentUserId: string | undefined;
  onUpdated: () => Promise<void>;
}

export const AccountActionsCell = ({
  account,
  currentUserId,
  onUpdated,
}: AccountActionsCellProps) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const isSelf = account.userId === currentUserId;
  const paused = isAccountPaused(account.userStatus);

  const run = async (action: () => Promise<void>, successTitle: string) => {
    setBusy(true);
    try {
      await action();
      await onUpdated();
      toast({ title: successTitle });
    } catch (err) {
      toast({
        title: "Update failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const personaValue =
    (account.workspacePersona as WorkspacePersona | null) ?? "leader";
  const appRoleValue: AppRole | "" =
    (account.roles[0] as AppRole | undefined) ?? "";

  return (
    <div className="flex flex-col gap-2 min-w-[11rem]">
      <Select
        disabled={busy}
        value={personaValue}
        onValueChange={(value) => {
          void run(() => setAccountPersona(account.userId, value), "Persona updated");
        }}
      >
        <SelectTrigger className="h-8 text-xs bg-white">
          <SelectValue placeholder="Persona" />
        </SelectTrigger>
        <SelectContent>
          {ADMIN_WORKSPACE_PERSONAS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        disabled={busy}
        value={appRoleValue === "" ? "__none__" : appRoleValue}
        onValueChange={(value) => {
          const roles =
            value === "__none__"
              ? []
              : [value as "teacher" | "principal" | "superintendent"];
          void run(() => setAccountAppRoles(account.userId, roles), "Org role updated");
        }}
      >
        <SelectTrigger className="h-8 text-xs bg-white">
          <SelectValue placeholder="Org role" />
        </SelectTrigger>
        <SelectContent>
          {ADMIN_APP_ROLES.map((opt) => (
            <SelectItem
              key={opt.value || "__none__"}
              value={opt.value === "" ? "__none__" : opt.value}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        disabled={busy || isSelf}
        value={account.userStatus}
        onValueChange={(value) => {
          void run(
            () => setAccountStatus(account.userId, value as AccountStatus),
            "Status updated"
          );
        }}
      >
        <SelectTrigger className="h-8 text-xs bg-white">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          {ADMIN_ACCOUNT_STATUSES.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex gap-1">
        {paused ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs flex-1"
            disabled={busy || isSelf}
            onClick={() => {
              void run(() => reactivateAccount(account.userId), "Account reactivated");
            }}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Reactivate"}
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs flex-1"
            disabled={busy || isSelf}
            onClick={() => {
              void run(() => pauseAccount(account.userId), "Account paused");
            }}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Pause"}
          </Button>
        )}
      </div>
      {isSelf && (
        <p className="text-[10px] text-gray-400 leading-tight">You cannot pause yourself.</p>
      )}
    </div>
  );
};
